import { useEffect, useState } from 'react';
import { describeWeather, WEATHER_FORECAST_MAX_DAYS } from '@camper-diary/shared';
import * as weatherApi from '../api/weather';

type Props = {
  tripId: string;
  stageId: string;
  plannedDate: string | null;
  /** Variante compatta (per popup mappa) */
  compact?: boolean;
};

type State =
  | { kind: 'noDate' }
  | { kind: 'past' }
  | { kind: 'tooFar' }
  | { kind: 'loading' }
  | { kind: 'ready'; weather: weatherApi.Weather }
  | { kind: 'empty' } // backend ha risposto null (es. dato non disponibile)
  | { kind: 'error'; msg: string };

function classifyDate(plannedDate: string | null): State['kind'] | null {
  if (!plannedDate) return 'noDate';
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const target = new Date(`${plannedDate}T00:00:00Z`);
  const diffDays = Math.round((target.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
  if (diffDays < 0) return 'past';
  if (diffDays > WEATHER_FORECAST_MAX_DAYS) return 'tooFar';
  return null; // valido, va caricato
}

export function StageWeatherBadge({ tripId, stageId, plannedDate, compact = false }: Props) {
  const [state, setState] = useState<State>(() => {
    const kind = classifyDate(plannedDate);
    return kind ? ({ kind } as State) : { kind: 'loading' };
  });
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const kind = classifyDate(plannedDate);
    if (kind) {
      setState({ kind } as State);
      return;
    }
    setState({ kind: 'loading' });
    let cancelled = false;
    weatherApi
      .getStageWeather(tripId, stageId)
      .then((w) => {
        if (cancelled) return;
        setState(w ? { kind: 'ready', weather: w } : { kind: 'empty' });
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setState({ kind: 'error', msg: e.message ?? 'Errore meteo' });
      });
    return () => {
      cancelled = true;
    };
  }, [tripId, stageId, plannedDate]);

  async function onRefresh() {
    setRefreshing(true);
    try {
      const w = await weatherApi.refreshStageWeather(tripId, stageId);
      setState(w ? { kind: 'ready', weather: w } : { kind: 'empty' });
    } catch (e) {
      setState({
        kind: 'error',
        msg: e instanceof Error ? e.message : 'Errore meteo',
      });
    } finally {
      setRefreshing(false);
    }
  }

  if (state.kind === 'noDate') return null;

  const cls = `weather-badge${compact ? ' compact' : ''}`;

  if (state.kind === 'past') {
    return <div className={`${cls} muted`}>(data passata)</div>;
  }
  if (state.kind === 'tooFar') {
    return (
      <div className={`${cls} muted`} title="Open-Meteo prevede al massimo 16 giorni">
        🌡️ previsioni non disponibili (&gt;16 giorni)
      </div>
    );
  }
  if (state.kind === 'loading') {
    return <div className={`${cls} muted`}>🌡️ caricamento meteo…</div>;
  }
  if (state.kind === 'error') {
    return (
      <div className={`${cls} err`}>
        Meteo non disponibile{' '}
        <button type="button" className="weather-refresh" onClick={() => void onRefresh()}>
          🔄
        </button>
      </div>
    );
  }
  if (state.kind === 'empty') {
    return (
      <div className={`${cls} muted`}>
        meteo n/d{' '}
        <button
          type="button"
          className="weather-refresh"
          onClick={() => void onRefresh()}
          disabled={refreshing}
        >
          {refreshing ? '…' : '🔄'}
        </button>
      </div>
    );
  }

  const w = state.weather;
  const desc = describeWeather(w.weatherCode);

  return (
    <div className={cls} title={`Meteo previsto per ${formatItDate(w.date)} (Open-Meteo)`}>
      <span className="w-icon" aria-hidden>
        {desc.icon}
      </span>
      <span className="w-temps">
        {Math.round(w.tmin)}° / {Math.round(w.tmax)}°
      </span>
      <span className="w-desc">{desc.it}</span>
      {w.precipitationMm > 0.1 && (
        <span className="w-precip">💧 {w.precipitationMm.toFixed(1)} mm</span>
      )}
      {w.windKmh > 20 && <span className="w-wind">💨 {Math.round(w.windKmh)} km/h</span>}
      {!compact && (
        <button
          type="button"
          className="weather-refresh"
          onClick={() => void onRefresh()}
          disabled={refreshing}
          title="Aggiorna previsione"
        >
          {refreshing ? '…' : '🔄'}
        </button>
      )}
    </div>
  );
}

function formatItDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
